// Status + source-type vocabulary for Technical Work, passed as classMap/
// labelMap overrides into the shared components/common/StatusBadge.
//
// The status enum is ON_HOLD / NOT_STARTED / ASSIGNED / ONGOING / COMPLETED
// / CLOSED, shared by all three source types - but ASSIGNED is reachable
// only by COMPLAINT and SERVICE_VISIT (assigning a technician skips the
// separate "Start" step for those two), and an Installation is the only
// type that starts life at ON_HOLD waiting to be pushed.
export const TECHNICAL_STATUS_CLASS = {
  ON_HOLD: "badge-linewarning",
  NOT_STARTED: "badge-linesecondary",
  ASSIGNED: "badge-lineinfo",
  ONGOING: "badge-lineinfo",
  COMPLETED: "badge-linesuccess",
  CLOSED: "badge-linesuccess",
};

export const TECHNICAL_STATUS_LABEL = {
  ON_HOLD: "On Hold",
  NOT_STARTED: "Not Started",
  ASSIGNED: "Assigned",
  ONGOING: "Ongoing",
  COMPLETED: "Completed",
  CLOSED: "Closed",
};

export const TECHNICAL_STATUS_FILTERS = [
  { value: "", label: "All Statuses" },
  { value: "ON_HOLD", label: "On Hold" },
  { value: "NOT_STARTED", label: "Not Started" },
  { value: "ASSIGNED", label: "Assigned" },
  { value: "ONGOING", label: "Ongoing" },
  { value: "COMPLETED", label: "Completed - awaiting verification" },
  { value: "CLOSED", label: "Closed" },
];

// All three source types share ONE ComplaintDescription column, so the LABEL
// is the only thing distinguishing a customer's own words from a visit brief
// from an installation scope. Ported from Flutter's
// presentation/widgets/technical_source_type.dart, which exists for exactly
// this reason - the list tile there used to classify rows as
// "isComplaint ? Complaint : Sales Order" and so labelled every Service
// Visit as a Sales Order.
export const TECHNICAL_SOURCE_LABEL = {
  COMPLAINT: "Complaint",
  SERVICE_VISIT: "Service Visit",
  SALES_ORDER: "Installation",
};

export const TECHNICAL_SOURCE_CLASS = {
  COMPLAINT: "badge-linedanger",
  SERVICE_VISIT: "badge-lineinfo",
  SALES_ORDER: "badge-linesuccess",
};

// What the shared description column means for each type, used as the field
// label on the detail screen.
export const TECHNICAL_DESCRIPTION_LABEL = {
  COMPLAINT: "What the customer reported",
  SERVICE_VISIT: "Visit brief",
  SALES_ORDER: "Installation scope",
};

export const TECHNICAL_SOURCE_FILTERS = [
  { value: "", label: "All Types" },
  { value: "COMPLAINT", label: "Complaints" },
  { value: "SERVICE_VISIT", label: "Service Visits" },
  { value: "SALES_ORDER", label: "Installations" },
];

// A finished row is frozen - the API refuses SetTechnicians / AddDiagnosis /
// UpsertServiceReport / CheckIn at COMPLETED and CLOSED so that nothing can
// change between the technician reporting done and the admin verifying it.
// Mirrored here so the web UI never offers a button the server will refuse.
export const isFinalized = (status) => status === "COMPLETED" || status === "CLOSED";

// Condition vocabulary on a product diagnosis row (read-only in web -
// diagnoses are recorded on-site against a scanned product).
export const DIAGNOSIS_CONDITION_LABEL = {
  FAULTY: "Faulty",
  NEEDS_REPLACEMENT: "Needs Replacement",
  REPAIRED: "Repaired",
  NEEDS_SERVICE: "Needs Service",
};

export const DIAGNOSIS_CONDITION_CLASS = {
  FAULTY: "badge-linedanger",
  NEEDS_REPLACEMENT: "badge-linewarning",
  REPAIRED: "badge-linesuccess",
  NEEDS_SERVICE: "badge-lineinfo",
};

// Activity timeline vocabulary. The timeline is DERIVED server-side from the
// actor columns the work/assignment rows already carry (plus real rows for
// description edits), so it is complete even for records created long before
// the log existed.
export const ACTIVITY_LABEL = {
  LOGGED: "Logged",
  DESCRIPTION_EDITED: "Description edited",
  PUSHED_TO_TECHNICAL: "Pushed to Technical",
  TECHNICIAN_ASSIGNED: "Technician assigned",
  TECHNICIAN_REMOVED: "Technician removed",
  STARTED: "Work started",
  ON_HOLD: "Put on hold",
  COMPLETED: "Completed",
  CLOSED: "Verified & closed",
};
